import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Divider, Menu } from '@mui/material'
import { State } from '../../store'
import { useSelector } from 'react-redux'
import { Icon } from '../Icon'
import { IconButton } from '../../buttons/IconButton'
import { deviceHiddenEventFilterOptionKeys, eventFilterOptions } from './eventTypes'
import { EventFilterIcon } from './EventFilterIcon'
import { EventTypeFilterMenuItem } from './EventTypeFilterMenuItem'

type Props = {
  device?: IDevice
  value?: IEventType[]
  onChange: (value?: IEventType[]) => void
}

export const EventTypeFilterMenu: React.FC<Props> = ({ device, value, onChange }) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const user = useSelector((state: State) => state.user)
  const selectedValues = value || []
  const activeTypes = useMemo(() => new Set(selectedValues), [selectedValues])
  const visibleOptions = useMemo(
    () =>
      device ? eventFilterOptions.filter(option => !deviceHiddenEventFilterOptionKeys.has(option.key)) : eventFilterOptions,
    [device]
  )
  const isFiltered = selectedValues.length > 0

  const handleToggle = (types?: IEventType[]) => {
    if (!types?.length) {
      onChange(undefined)
      return
    }

    const next = new Set(activeTypes)
    const isSelected = types.every(type => next.has(type))

    if (isSelected) types.forEach(type => next.delete(type))
    else types.forEach(type => next.add(type))

    onChange(next.size ? Array.from(next) : undefined)
  }

  const handleClose = () => setAnchorEl(null)

  return (
    <>
      <IconButton
        title={t('eventTypeFilterMenu.filterEvents', 'Filter events')}
        name="filter"
        color="grayDarker"
        modified={isFiltered}
        onClick={event => setAnchorEl(event.currentTarget as HTMLButtonElement)}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableScrollLock
        autoFocus={false}
        elevation={2}
      >
        <EventTypeFilterMenuItem
          label={t('eventTypeFilterMenu.all', 'All')}
          selected={!isFiltered}
          onClick={() => handleToggle(undefined)}
          icon={<Icon name="asterisk" size="md" color={!isFiltered ? 'primary' : 'gray'} />}
        />
        <Divider />
        {visibleOptions.map(option => {
          const selected = option.types.every(type => activeTypes.has(type))

          return (
            <EventTypeFilterMenuItem
              key={option.key}
              label={option.label}
              selected={selected}
              onClick={() => handleToggle(option.types)}
              icon={<EventFilterIcon option={option} user={user} selected={selected} />}
            />
          )
        })}
      </Menu>
    </>
  )
}
